import React, { useState } from 'react';
import { Loader, Table, theme } from '@fdha/web-ui-library';
import {
  Paper as MuiPaper,
  styled,
  TableCell,
  TableRow,
  Box,
  Typography,
} from '@mui/material';
import { grey } from '@mui/material/colors';
import { Button } from '@fdha/web-ui-library';
import { Link, useParams } from 'react-router-dom';
import Icon from 'react-eva-icons';
import {
  SurveyQuestion,
  SurveyQuestionType,
  SurveyResponse,
  useGetProfileQuery,
  useGetSurveyInstanceQuery,
  AssignmentType,
  SurveyStatus,
} from '@fdha/graphql-api-admin';

import { getSurveyName } from '../../../../utils/surveysData';
import {
  getSurveyResponsesHeaderCells,
  renderSurveyResponsesTableCells,
} from '../../../../utils';

import SurveyQuestionView from './SurveyQuestionView';
import AnswerRequiredSurveys from './AnswerRequiredSurveys';

const Paper = styled(MuiPaper)(({ theme }) => ({
  padding: '12px 24px',
  maxWidth: '857px',
  width: '100%',
  marginBottom: '24px',
  boxShadow: theme.shadows[2],
  alignSelf: 'center',
}));

const SurveyView = () => {
  const { patientId, surveyId } = useParams();
  const [isEditing, setIsEditing] = useState(false);
  const { data, loading, refetch } = useGetSurveyInstanceQuery({
    variables: {
      patientId: patientId || '',
      instanceId: surveyId || '',
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: profileData } = useGetProfileQuery();
  const userId = profileData?.me.id;

  const handleEditClick = () => {
    setIsEditing(true);
  };

  if (loading) {
    return <Loader />;
  }

  if (!data?.surveyInstance) {
    return <Typography>{`No survey found with ID ${surveyId}`}</Typography>;
  }

  const survey = {
    ...data.surveyInstance,
    name: getSurveyName(
      data.surveyInstance.name,
      data.surveyInstance.isStructured
    ),
  };
  const label = `${survey.name} ${survey.label ? '- ' + survey.label : ''}`;
  const rows: SurveyResponse[] = [
    {
      id: survey.id,
      label,
      assignment_type: AssignmentType.Required,
      survey_date: new Date(Number(survey.due_at)).toISOString(),
      survey_status: survey.status || SurveyStatus.Open,
      completed_at: survey.completed_at_patient_tz,
      completed_by: survey.created_by,
      created_by_name: survey.created_by_name,
    },
  ];

  const renderRow = (row?: SurveyResponse) => {
    return (
      <TableRow hover key={row?.label} data-testid="TABLE_ROW">
        <TableCell>
          <Link to="../" data-testid="SURVEYS_PREVIEW_BACK_BUTTON">
            <Icon
              name="arrow-ios-back-outline"
              fill={grey[600]}
              size="large"
              data-testid="SURVEYS_BUTTON"
            />
          </Link>
        </TableCell>
        {renderSurveyResponsesTableCells(row, userId, patientId)}
      </TableRow>
    );
  };

  const displayAddResponseButton =
    !data.surveyInstance.completed_at && !isEditing;
  const enableAnswerRequiredSurveys =
    !data.surveyInstance.completed_at && isEditing;

  return (
    <>
      <MuiPaper data-testid="SURVEYS_PREVIEW_TABLE" sx={{ marginBottom: 5 }}>
        <Table<SurveyResponse>
          actions="left"
          headCells={getSurveyResponsesHeaderCells()}
          renderRow={renderRow}
          rows={rows}
        />
      </MuiPaper>
      {displayAddResponseButton && (
        <Box display="flex" justifyContent="flex-start" marginBottom={3}>
          <Button
            variant="contained"
            color="primary"
            startEvaIcon={{
              name: 'edit-2-outline',
              fill: theme.palette.primary.contrastText,
            }}
            component={Link}
            onClick={handleEditClick}
            data-testid="SURVEY_ADD_RESPONSE_BUTTON"
            size="medium"
          >
            Add response
          </Button>
        </Box>
      )}
      {enableAnswerRequiredSurveys ? (
        <AnswerRequiredSurveys
          surveyInstance={data.surveyInstance}
          patientId={patientId || ''}
          setIsEditing={setIsEditing}
          onSuccess={() => refetch()}
        />
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          data-testid="SURVEY_PREVIEW_CONTAINER"
        >
          {data?.surveyInstance.questions
            ?.filter(
              (question) => question.type !== SurveyQuestionType.Explanation
            )
            .map((item: SurveyQuestion, index: number) => (
              <Paper data-testid="SURVEY_QUESTION_CONTAINER" key={item.id}>
                <SurveyQuestionView item={item} questionNumber={index} />
              </Paper>
            ))}
        </Box>
      )}
    </>
  );
};

export default SurveyView;
